import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { fxGadgetFire } from '../render/weapon-fx';
import { disposeGadget, makeDrone, makeFieldRing, updateFieldRing } from '../render/gadgets';
import { damageEnemy, nearestEnemy, spawnRing } from '../sim/fx';
import { spawnBullet } from '../sim/weapons';
import { safeSpot } from '../world/arena';
import { P } from '../world/player';
import { mines } from '../world/pools';
import type { Evolution } from './evolutions';
/* ============================================================
   進化 · 第二批（2026-08-05）
   ============================================================

   設計原則與整批的動機寫在 `data/evolutions.ts`，這裡只有定義。

   這一批補的是**覆蓋率**：在這之前只有 4 把武裝有進化路線，
   質變因此全部發生在第 4–6 分鐘（材料一用完就沒有下一個了）。

   六條裡有一條是 `w-mine` 的**第二條路線**：
   磁暴雷場要你**站住**（腳下一片雷毯），磁吸黏雷要你**追**（雷貼在敵人身上）。
   同一把武裝的兩條路線必須有這種等級的體感差異，否則只是換一組數字。

   **這一批不碰 `render/`**：新武裝的視覺一律重用既有的 gadget
   （無人機、場界環）與 `sim/fx` 的環／碎片。視覺層是使用者自己在做的線。 */

export var EVOLUTIONS_B: Evolution[] = [
  {
    /* 衝擊斬波 + 延壽迴路 → 刃幕壁壘。
       **拿掉的限制：「斬波是一瞬間掠過的線」。**
       原本它飛得比敵人快得多，所以敵人只要不站在那 0.1 秒的線上就完全安全 ——
       它的覆蓋是一條線乘上一瞬間。進化之後速度砍到接近靜止、存活五秒，
       **覆蓋從「線」變成「一面推進的牆」**：你放下去的是地形，不是一發子彈。 */
    id: 'e-bladewall', n: '刃幕壁壘', kind: '進化', axes: ['範圍', '持續'],
    evo: { from: 'w-pulse', needs: 'p-dur' },
    turn: 3, turnD: '只朝前的牆 → 環繞整圈的包圍網（背後不再安全）',
    d: function (lv) {
      return '**斬波不再一瞬間掠過**：' + (2 + lv) + ' 道刃幕以極慢的速度推進，持續掃過整片戰場'
        + (lv < 3 ? '' : '，**環繞整個圓周，不再只朝前**');
    },
    cd: function () { return 0.9; },
    fire: function (lv) {
      /* 速度 4.5：比最慢的雜兵還慢一點，所以是**敵人穿過牆**而不是牆追著敵人。
         `pierce` 給 99 是為了不被第一排吃掉 —— 但穿透每次會讓傷害 ×0.75
         （`sim/projectiles.ts`），所以它天然不會對深處的敵群過強。

         等級（2026-08-05）：
           Lv2  3 → 4 道（顆數 +33%），仍只朝前
           Lv3  5 道且**改成環繞整圈**——拿掉第二條限制：壁壘只朝前推進，
                貼身側翼或繞背的敵人完全安全。5/3 ≈ 1.67x。 */
      var angs = lv < 3
        ? (lv < 2 ? [0, 0.85, -0.85] : [0, 0.85, -0.85, 1.7])
        : [0, TAU / 5, -TAU / 5, TAU * 2 / 5, -TAU * 2 / 5];
      var crit = RNG.next() < P.s.crit;
      for (var i = 0; i < angs.length; i++) {
        var b = spawnBullet(P.pos.x, P.pos.z, P.faceAim + angs[i],
          118 * (crit ? P.s.critMul : 1), crit, 4.5, 99, 0, 4.2 * (P.s.area || 1), 0.6, 'e-bladewall');
        if (b) b.ttl = 5 * (P.s.dur || 1);
      }
      SFX.slash();
    }
  },
  {
    /* 感應無人機 + 供彈加速 → 獵殺蜂群。
       **拿掉的限制：「僚機必須待在你身邊」。**
       原本兩台繞著玩家 4.2 單位飛，射程 30 —— 它的攻擊範圍其實是
       **玩家的位置**，你退它就打不到。進化之後四台各自脫離編隊、飛向自己的獵物，
       **輸出範圍與玩家位置脫鉤**：你在跑，它們仍然在打。 */
    id: 'e-swarm', n: '獵殺蜂群', kind: '進化', axes: ['冷卻', '傷害'],
    evo: { from: 'w-drone', needs: 'p-rate' },
    turn: 3, turnD: '四台編隊上限 → 第五台加入（同時多咬一個目標）',
    d: function (lv) {
      return '**僚機脫離編隊**：' + (lv < 3 ? '四' : '五') + '台各自飛向獵物獵殺，射程不再受你的位置限制'
        + (lv < 2 ? '' : '，攻擊間隔更短') + (lv < 3 ? '' : '；**第五台加入編隊**');
    },
    onEquip: function (o) {
      o.st = { d: [makeDrone(0x5fe3ff), makeDrone(0xffd24a), makeDrone(0x9fffe8), makeDrone(0xff8a3d), makeDrone(0xff4fa3)], cd: [0, 0, 0, 0, 0], t: 0 };
      /* 初始散開：四台疊在同一點的話前幾秒會一起追同一隻。
         第五台（Lv3 才啟用）先停在原地、設不可見——`onEquip` 只在拿到的那一刻跑一次，
         沒有機會等升到 Lv3 才生成，只能先建好再靠 `update` 切可見度。 */
      for (var i = 0; i < 4; i++) {
        var a = (i / 4) * TAU;
        o.st.d[i].g.position.set(P.pos.x + Math.cos(a) * 6, 3.2, P.pos.z + Math.sin(a) * 6);
      }
      o.st.d[4].g.position.set(P.pos.x, 3.2, P.pos.z);
      o.st.d[4].g.visible = false;
    },
    onRemove: function (o) {
      if (o.st) for (var i = 0; i < o.st.d.length; i++) disposeGadget(o.st.d[i]);
      o.st = null;
    },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var st = o.st, n = lv < 3 ? 4 : 5;
      st.t += dt;
      st.d[4].g.visible = lv >= 3;
      for (var i = 0; i < n; i++) {
        var g = st.d[i].g;
        /* 各自找**自己最近的**（不是玩家最近的）—— 四台散開之後這自然會分開目標。
           射程 70 幾乎等於整個場地：這一條就是「與玩家位置脫鉤」的實作。 */
        var tgt = nearestEnemy(g.position, 70, undefined, false);
        var tx, tz;
        if (tgt) {
          /* 停在目標外圍 7 單位，不要飛進敵人裡面（讀起來會像撞上去）。 */
          var dx = g.position.x - tgt.pos.x, dz = g.position.z - tgt.pos.z;
          var d = Math.max(0.001, Math.hypot(dx, dz));
          tx = tgt.pos.x + dx / d * 7; tz = tgt.pos.z + dz / d * 7;
        } else {
          var a2 = st.t * 0.8 + (i / n) * TAU;
          tx = P.pos.x + Math.cos(a2) * 6; tz = P.pos.z + Math.sin(a2) * 6;
        }
        var k = Math.min(1, dt * 2.2);
        g.position.x += (tx - g.position.x) * k;
        g.position.z += (tz - g.position.z) * k;
        g.position.y = 3.2 + Math.sin(st.t * 2 + i) * 0.3;
        if (tgt) g.lookAt(tgt.pos.x, g.position.y, tgt.pos.z);
        st.cd[i] -= dt;
        st.d[i].muzzle.visible = st.cd[i] > 0.26;
        if (st.cd[i] > 0 || !tgt) continue;
        /* 等級（2026-08-05）：Lv2 攻擊間隔 0.32 → 0.24（+33%）；Lv3 間隔不再縮短，
           改成第五台加入戰場——拿掉「編隊規模固定，同時只能咬 4 個目標」這條限制。
           （4 台×1/0.32 → 5 台×1/0.24 ≈ 1.67x） */
        st.cd[i] = lv < 2 ? 0.32 : 0.24;
        var a3 = Math.atan2(tgt.pos.x - g.position.x, tgt.pos.z - g.position.z);
        var crit = RNG.next() < P.s.crit;
        spawnBullet(g.position.x, g.position.z, a3,
          108 * (crit ? P.s.critMul : 1), crit, 62, 0, 0, 0.5, undefined, 'e-swarm');
      }
    }
  },
  {
    /* 斥力力場 + 爆壓擴散 → 湮滅力場。
       **拿掉的限制：「推開」—— 它 Lv3 之後在趕走自己的獵物。**
       原本的力場把敵人推出去，而傷害只發生在環內：這把武裝的兩半在互相打架，
       推得越好、打得越少。進化之後**推力反轉成吸力**，敵人被鎖在環裡反覆挨打。
       它從「不要靠近我」變成「你出不去」，站位邏輯整個翻面。 */
    id: 'e-collapse', n: '湮滅力場', kind: '進化', axes: ['範圍', '衝擊'],
    evo: { from: 'w-aura', needs: 'p-area' },
    turn: 3, turnD: '範圍固定，抓不到稍微外圍的敵人 → 範圍擴大 24%',
    d: function (lv) {
      return '**推力反轉成吸力**：範圍內的敵人被拖向核心，出不去，持續湮滅'
        + (lv < 2 ? '' : '，結算更密') + (lv < 3 ? '' : '；**範圍擴大，連外圍的敵人也拖得到**');
    },
    onEquip: function (o) { o.st = { t: 0, vis: makeFieldRing(0xff8a3d) }; },
    onRemove: function (o) { if (o.st) disposeGadget(o.st.vis); o.st = null; },
    update: function (lv, dt, o) {
      if (!o.st) return;
      /* ⚠️ **拉力（kx/kz 的倍率）刻意不隨等級調高。** 第一版試過 13 → 22，
         `pnpm crafts` 量到 ×0.22——不是變弱，是敵人被拉力甩出 `R` 之外
         （`d` 越靠近 0.6 這個下限，`k=1/max(0.6,d)` 越大，衝量在中心附近
         會被放大到直接把敵人拋出場外，之後要走好幾秒才回得來）。
         拉力乘一次尚可，乘到 22 會共振甩飛，所以 Lv3 改動**半徑**，不動拉力。

         等級（2026-08-05）：
           Lv2  結算間隔 0.35 → 0.27（+30%）
           Lv3  間隔再壓到 0.20，且**半徑 10.5 → 13（+24%）**——
                拿掉「範圍固定，抓不到稍微外圍的敵人」這條限制。
                （0.35/0.20 ≈ 1.75x，半徑加成未計入） */
      var R = (lv < 3 ? 10.5 : 13) * (P.s.area || 1);
      /* **環的視覺每幀更新，傷害結算不是。** 這兩行分開的順序很重要 ——
         ⚠️ 2026-08-05 一度把下面那道節流閘門刪掉，於是 39 點傷害從
         「每 0.35 秒一次」變成**每幀一次**（60×/秒），量測台讀到 ×70.54。
         那個數字大到看起來像量測台壞了，其實是這把武裝真的在每幀結算。 */
      updateFieldRing(o.st.vis, P.pos.x, P.pos.z, R, dt);
      o.st.t -= dt;
      if (o.st.t > 0) return;
      o.st.t = lv < 2 ? 0.35 : (lv < 3 ? 0.27 : 0.20);
      spawnRing(P.pos.x, P.pos.z, 0xff8a3d, R * 1.05, R * 0.3, 0.3);
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
        if (d > R + e.r) continue;
        var k = 1 / Math.max(0.6, d);
        damageEnemy(e, 39, false, -dx * k * 13, -dz * k * 13, 'vortex', undefined, undefined, 'e-collapse');
      }
    }
  }
];
